/**
 * Barra de precio con dos tiradores.
 *
 * Horizon sirve el filtro de precio como dos campos de texto -«desde» y
 * «hasta»-. Funcionan, pero obligan a escribir cifras para algo que se decide
 * de un vistazo: cuanto quiero gastarme. Las tres referencias del brief usan
 * barra.
 *
 * NO SE TOCA `snippets/price-filter.liquid`, que es del tema y se pierde en la
 * siguiente actualizacion de Horizon. Este script AÑADE la barra encima de los
 * campos que ya existen y los usa como fuente de verdad: al mover un tirador se
 * escribe en el campo y se dispara su `change`, que es exactamente lo que
 * Horizon ya escucha para relanzar el filtrado. Si manana el tema cambia como
 * aplica los filtros, la barra sigue funcionando porque no sabe nada de eso.
 *
 * Y si el script no carga, los campos siguen ahi y el filtro sigue sirviendo.
 * La barra es una capa encima, no un reemplazo.
 */
(() => {
  const PASO = 1;
  // Los textos, traducidos por snippets/natnatura-textos-js.liquid: un .js
  // no tiene filtro `t` y en /pt saldrian en castellano.
  const T = window.NN_TEXTOS || {};

  /** El maximo del rango, que Horizon publica en el marcador de estado. */
  const techo = (panel) => {
    const marca = panel.querySelector('[data-range-max]');
    if (marca) {
      const n = parseFloat(String(marca.dataset.rangeMax).replace(/\./g, '').replace(',', '.'));
      if (Number.isFinite(n) && n > 0) return Math.ceil(n);
    }
    // Respaldo: el propio marcador de posicion del campo «hasta».
    const hasta = panel.querySelector('input[name$="price.lte"]');
    const p = hasta && parseFloat(String(hasta.placeholder).replace(',', '.'));
    return Number.isFinite(p) && p > 0 ? Math.ceil(p) : 100;
  };

  const numero = (v, porDefecto) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : porDefecto;
  };

  const monta = (panel) => {
    const desde = panel.querySelector('input[name$="price.gte"]');
    const hasta = panel.querySelector('input[name$="price.lte"]');
    if (!desde || !hasta) return;

    /* ¿YA TIENE BARRA? Y la pregunta hay que hacerla bien, que aqui estuvo el
       fallo gordo: la comprobacion miraba `panel.querySelectorAll('.nn-barra-precio')`,
       pero la barra se inserta ANTES del grupo de campos, que puede colgar
       fuera del panel. Nunca la encontraba, asi que montaba otra en cada pase
       -y con un repaso por segundo eso son sesenta barras por minuto-. Se
       vieron cuatro y subiendo, y es lo que dejo la pestana sin responder.

       Se guarda la referencia en el propio panel y se comprueba que sigue
       colgando del documento: si Horizon repinta y se la lleva, `isConnected`
       pasa a false y se vuelve a montar. */
    if (panel.nnCaja && panel.nnCaja.isConnected) return;
    if (panel.nnCaja) panel.nnCaja.remove();

    const max = techo(panel);

    const caja = document.createElement('div');
    caja.className = 'nn-barra-precio';
    caja.innerHTML =
      `<div class="nn-barra-precio__pista"><span class="nn-barra-precio__tramo"></span></div>` +
      `<input class="nn-barra-precio__mando nn-barra-precio__mando--min" type="range" min="0" max="${max}" step="${PASO}">` +
      `<input class="nn-barra-precio__mando nn-barra-precio__mando--max" type="range" min="0" max="${max}" step="${PASO}">`;
    // La etiqueta por `setAttribute` y no dentro del HTML: asi una comilla en
    // una traduccion no puede romper el marcado.
    caja.querySelector('.nn-barra-precio__mando--min').setAttribute('aria-label', T.precio_minimo || 'Precio mínimo');
    caja.querySelector('.nn-barra-precio__mando--max').setAttribute('aria-label', T.precio_maximo || 'Precio máximo');

    const campos = desde.closest('.price-facet, .facets__price') || desde.parentElement.parentElement;
    campos.parentElement.insertBefore(caja, campos);
    panel.nnCaja = caja;

    const min$ = caja.querySelector('.nn-barra-precio__mando--min');
    const max$ = caja.querySelector('.nn-barra-precio__mando--max');
    const tramo = caja.querySelector('.nn-barra-precio__tramo');

    const pinta = () => {
      const a = Number(min$.value);
      const b = Number(max$.value);
      tramo.style.insetInlineStart = `${(a / max) * 100}%`;
      tramo.style.insetInlineEnd = `${100 - (b / max) * 100}%`;
    };

    /** De los campos a la barra. */
    const desdeCampos = () => {
      min$.value = String(Math.max(0, Math.min(max, numero(desde.value, 0))));
      max$.value = String(Math.max(0, Math.min(max, numero(hasta.value, max))));
      pinta();
    };

    /** De la barra a los campos. Se avisa con `change`, que es lo que Horizon oye. */
    let espera;
    const aCampos = () => {
      let a = Number(min$.value);
      let b = Number(max$.value);
      if (a > b) { const t = a; a = b; b = t; min$.value = String(a); max$.value = String(b); }
      pinta();
      desde.value = a > 0 ? String(a) : '';
      hasta.value = b < max ? String(b) : '';
      window.clearTimeout(espera);
      // Se espera a que el dedo pare: sin esto, arrastrar el tirador lanzaria
      // una peticion de filtrado por cada pixel.
      espera = window.setTimeout(() => {
        desde.dispatchEvent(new Event('change', { bubbles: true }));
        hasta.dispatchEvent(new Event('change', { bubbles: true }));
      }, 400);
    };

    min$.addEventListener('input', aCampos);
    max$.addEventListener('input', aCampos);
    desde.addEventListener('change', desdeCampos);
    hasta.addEventListener('change', desdeCampos);

    desdeCampos();
  };

  let dentro = false;

  const barre = () => {
    /* GUARDA DE REENTRADA. Montar la barra MUTA el DOM, asi que cualquier cosa
       que vigile el DOM se dispara con nuestra propia mutacion. */
    if (dentro) return;
    dentro = true;
    try {
      /* Se recorre por el CAMPO y no por el panel. Barriendo
         `.facets__panel, .price-facet` el mismo bloque casaba con los dos
         selectores -uno contiene al otro- y montaba dos barras en cada sitio:
         cuatro en total para dos filtros de precio. El campo `gte` es unico
         por filtro, asi que sirve de llave. */
      document.querySelectorAll('input[name$="price.gte"]').forEach((campo) => {
        const panel = campo.closest('.facets__panel') || campo.closest('.price-facet');
        if (panel) monta(panel);
      });
    } finally {
      dentro = false;
    }
  };

  /*
   * SIN `MutationObserver`, Y ESTO NO ES PEREZA.
   *
   * La primera version vigilaba `document.body` con `subtree: true` para
   * volver a montar la barra cuando Horizon repinta el panel al filtrar.
   * Montar la barra muta el DOM, el observador se despertaba con esa misma
   * mutacion y el ciclo montar → mutar → observar → montar dejo la pestana sin
   * responder. Paso de verdad: hubo que recuperarla.
   *
   * Un repaso cada segundo hace el mismo trabajo -el panel vuelve del servidor
   * y en el siguiente pase se le pone la barra- y no puede realimentarse,
   * porque no depende de lo que el propio codigo cambie. Para una columna de
   * filtros, un segundo de retardo no lo nota nadie.
   */
  const inicia = () => {
    barre();
    window.setInterval(barre, 1000);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', inicia, { once: true });
  } else {
    inicia();
  }
})();
